import * as Sentry from '@sentry/react'

/**
 * `TypeError: Failed to fetch`는 자체 백엔드 장애일 때만 의미가 있다.
 * 모든 실패 fetch/xhr 브레드크럼을 검사하여 자체 origin 실패가 1건이라도 있으면
 * 보고(false)하고, 모든 후보가 외부 도메인일 때만 drop(true)한다.
 *
 * 가장 최근 1건만 보면 동시 다발 fetch에서 외부가 더 늦게 실패할 경우
 * 자체 origin 실패가 silent drop되는 race가 발생하므로, 자체 후보가 1건이라도
 * 있으면 보고하는 보수적 정책을 채택한다.
 *
 * 응답 status가 1 이상이면 fetch 자체는 도달한 것이므로 동일 브레드크럼으로 인한
 * "Failed to fetch"가 아니라고 보고 다음 후보로 넘어간다.
 */
export const isExternalFetchFailure = (event: Sentry.ErrorEvent): boolean => {
  const breadcrumbs = event.breadcrumbs ?? []
  const selfOrigin = window.location.origin
  let foundFailedFetch = false

  for (let i = breadcrumbs.length - 1; i >= 0; i--) {
    const b = breadcrumbs[i]
    if (b.category !== 'fetch' && b.category !== 'xhr') continue

    const data = b.data as { url?: string; status_code?: number } | undefined
    const status = data?.status_code
    if (status != null && status > 0) continue

    const url = data?.url
    if (url == null || url.length === 0) continue

    try {
      const u = new URL(url, selfOrigin)
      if (u.origin === selfOrigin) return false
      foundFailedFetch = true
    } catch {
      return false
    }
  }
  return foundFailedFetch
}

/**
 * 보고되는 이벤트에 가장 최근 실패한 fetch/xhr 컨텍스트를 부착한다.
 * triage 시 5-5(동시 다발 fetch에서 origin 오판) 의심 케이스를 즉시 검증할 수 있게 한다.
 *
 * PII 보호:
 * - URL은 `origin + pathname`만 보존 (query string·hash 제거)
 * - tag(`failedFetchOrigin`)는 origin 단위로 카디널리티 제한
 * - 상세 path는 인덱싱되지 않는 contexts에 저장
 */
export const enrichFetchFailureEvent = (event: Sentry.ErrorEvent): void => {
  const breadcrumbs = event.breadcrumbs ?? []
  if (breadcrumbs.length === 0) return

  const selfOrigin = window.location.origin
  const origins: string[] = []
  const paths: string[] = []
  const MAX_ENTRIES = 3

  for (let i = breadcrumbs.length - 1; i >= 0 && paths.length < MAX_ENTRIES; i--) {
    const b = breadcrumbs[i]
    if (b.category !== 'fetch' && b.category !== 'xhr') continue

    const data = b.data as { url?: string; status_code?: number } | undefined
    if (data?.status_code != null && data.status_code > 0) continue

    const url = data?.url
    if (url == null || url.length === 0) continue

    try {
      const u = new URL(url, selfOrigin)
      const originLabel = u.origin === selfOrigin ? 'self' : u.origin
      if (!origins.includes(originLabel)) {
        origins.push(originLabel)
      }
      paths.push(`${originLabel}${u.pathname}`)
    } catch {
      // URL 파싱 실패는 무시 — 보고만 보장하면 됨
    }
  }

  if (paths.length === 0) return

  event.tags = {
    ...(event.tags ?? {}),
    failedFetchOrigin: origins.join(','),
  }
  event.contexts = {
    ...(event.contexts ?? {}),
    failedFetch: { paths },
  }
}

/**
 * 소음성 이벤트 여부 판별. 다음 경우 true 반환:
 * - 사용자 취소로 인한 AbortError
 * - 크로스오리진 frame 접근 SecurityError
 * - IndexedDB 연결 종료 InvalidStateError
 * - IndexedDB 트랜잭션 비활성/연결 손실/사용자 삭제로 인한 추가 IDB 노이즈
 * - React reconciler의 NotFoundError(removeChild) — 외부 DOM 변경으로 발생
 * - 토큰 재발급 실패 (TokenRequestFailedError + restClient throw 패턴 메시지)
 *   — 사용자 액션이 필요한 인증 에러로 자동 재로그인 플로우에서 별도 처리
 * - 서버 보안 경고/세션 종료 응답 — 인증 플로우의 비즈니스 메시지
 * - IP allowlist 차단 (ip-not-allowed) — jwt.handleIpNotAllowed가 access-denied 이동을
 *   이미 처리하므로 unhandled rejection은 노이즈 (OFFICEMAIL-WEB-ACCESS-KE)
 * - EML 첨부 다운로드 실패 (file not exist) — string reject로 인한 Non-Error rejection
 * - 외부 도메인 호출 실패 TypeError(Failed to fetch) — 광고/추적 차단, 외부 SDK 등
 *   (자체 origin 호출 실패는 진짜 백엔드/CORS 문제일 수 있어 그대로 보고)
 * - stacktrace 없는 IDBRequest/XHR ProgressEvent (OFFICEMAIL-WEB-ACCESS-7, -5S 패턴)
 *
 * onunhandledrejection으로 보고된 DOMException은 hint.originalException이 Error 인스턴스가
 * 아닐 수 있어 event.exception.values[0]의 type/value도 함께 검사한다.
 */
export const isNoisyEvent = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean => {
  const original = hint.originalException
  const err =
    original instanceof Error ? (original as Error & { code?: string; status?: number }) : undefined
  const code = err?.code ?? ''

  const exc = event.exception?.values?.[0]
  const name = err?.name ?? exc?.type ?? ''
  const message = `${err?.message ?? ''} ${exc?.value ?? ''}`

  if (name === 'AbortError') return true
  if (code === 'TOKEN_REQUEST_FAILED') return true
  // restClient가 일반 Error('token request failed')로 reject하는 경로 (TokenRequestFailedError 미사용)
  if (/token request failed/i.test(message)) return true
  // 서버가 토큰 revoke를 응답 메시지로 전달 — restClient.processResponse에서 handleTokenRevoked로
  // 자동 로그아웃 처리하므로 Sentry에는 노이즈로 분류
  if (/token has been revoked/i.test(message)) return true
  // 서버가 보안 경고/세션 종료를 응답 메시지로 throw — 자동 로그아웃 플로우로 처리되어야 할 비즈니스 메시지
  if (/Security alert.*Session terminated/i.test(message)) return true
  // IP allowlist 차단 — handleIpNotAllowed가 access-denied 이동을 이미 처리하므로
  // reject(new Error('ip-not-allowed'))의 unhandled rejection은 노이즈 (OFFICEMAIL-WEB-ACCESS-KE)
  if (/ip-not-allowed/i.test(message)) return true
  // REST 경로 서버 요청 한도 초과(429) — restClient.handleRateLimited가 toast로 안내하므로 노이즈.
  // status 기반 매칭(메시지 정규식 지양): RestClientError는 HTTP status를 보존하며 hint.originalException으로 전달됨.
  // 주의: OFFICEMAIL-WEB-ACCESS-JR은 JMAP jam client 경로(RestClientError 아님)라 이 REST 필터로는 미커버 — 7차 이월.
  if (err?.name === 'RestClientError' && err.status === 429) return true
  // 첨부 다운로드 경로의 string reject('file not exist.') — Non-Error rejection 으로 exc.value 에 들어옴
  if (/file not exist/i.test(message)) return true
  if (name === 'SecurityError' && /cross-origin frame/i.test(message)) return true
  if (name === 'InvalidStateError' && /database connection is closing/i.test(message)) return true
  // 추가 IDB 노이즈: 브라우저/사용자가 DB 상태를 외부에서 변경하여 앱이 제어 불가
  if (name === 'TransactionInactiveError') return true
  if (/Connection to Indexed Database server lost/i.test(message)) return true
  if (/Database deleted by request of the user/i.test(message)) return true
  if (name === 'NotFoundError' && /removeChild/i.test(message)) return true
  if (name === 'TypeError' && /Failed to fetch/i.test(message) && isExternalFetchFailure(event)) {
    return true
  }

  // Error 인스턴스도 아니고 stacktrace도 없는 익명 reject (IDBRequest / XHR ProgressEvent)
  // Error 인스턴스인 reject는 라이브러리발 실제 버그일 수 있으므로 drop하지 않음
  const frames = exc?.stacktrace?.frames
  const hasStacktrace = frames != null && frames.length > 0
  const mechanism = exc?.mechanism?.type ?? ''
  const eventMessage = event.message ?? ''
  const isPromiseRejectionWrapper =
    /captured as promise rejection|Non-Error promise rejection captured/i.test(
      `${eventMessage} ${message}`,
    )
  if (
    err == null &&
    !hasStacktrace &&
    (mechanism === 'onunhandledrejection' || isPromiseRejectionWrapper)
  ) {
    return true
  }

  return false
}
